import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth, API, authFetch } from "@/App";
import { toast } from "sonner";

import { 
  ArrowLeft, Plus, DollarSign, TrendingUp,
  Users, Target, BarChart3, Trash2, Edit,
  ArrowUpRight, ArrowDownRight, Minus, Download
} from "lucide-react";

const SimpleBarChart = ({ data, formatCurrency }) => {
  if (!data || data.length === 0) return null;
  const maxRevenue = Math.max(...data.map(d => d.revenue));
  
  return (
    <div className="space-y-2">
      {data.slice(-8).map((item, idx) => (
        <div key={idx} className="flex items-center gap-3">
          <span className="text-xs text-neutral-500 w-16">{item.period}</span>
          <div className="flex-1 h-6 bg-neutral-100 rounded overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-emerald-500 to-emerald-400 rounded transition-all"
              style={{ width: `${(item.revenue / maxRevenue) * 100}%` }}
            />
          </div>
          <span className="text-xs font-medium text-neutral-700 w-20 text-right">
            {formatCurrency(item.revenue)}
          </span>
        </div>
      ))}
    </div>
  );
};

export const SalesTrackingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sales, setSales] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [selectedYear, setSelectedYear] = useState("all");
  
  const currentYear = new Date().getFullYear();
  const years = [currentYear, currentYear - 1, currentYear - 2, currentYear - 3];
  
  const [formData, setFormData] = useState({
    year: currentYear,
    quarter: 1,
    revenue: "",
    expenses: "",
    units_sold: "",
    new_customers: "",
    returning_customers: "",
    notes: ""
  });

  useEffect(() => {
    fetchData();
  }, [selectedYear]);

  const fetchData = async () => {
    try {
      const [salesRes, summaryRes] = await Promise.all([
        authFetch(`${API}/sales${selectedYear !== "all" ? `?year=${selectedYear}` : ""}`, {  }),
        authFetch(`${API}/sales-summary`, {  })
      ]);
      
      if (salesRes.ok) setSales(await salesRes.json());
      if (summaryRes.ok) setSummary(await summaryRes.json());
    } catch (_error) {
      console.error("Error fetching data:", _error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        year: parseInt(formData.year),
        quarter: parseInt(formData.quarter),
        revenue: parseFloat(formData.revenue) || 0,
        expenses: parseFloat(formData.expenses) || 0,
        units_sold: parseInt(formData.units_sold) || 0,
        new_customers: parseInt(formData.new_customers) || 0,
        returning_customers: parseInt(formData.returning_customers) || 0,
        notes: formData.notes
      };

      const response = await authFetch(`${API}/sales`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(editingEntry ? "Sales data updated!" : "Sales data added!");
        setShowDialog(false);
        resetForm();
        fetchData();
      } else {
        toast.error("Failed to save sales data");
      }
    } catch (_error) {
      toast.error("An error occurred");
    }
  };

  const handleDelete = async (salesId) => {
    if (!confirm("Delete this sales entry?")) return;
    
    try {
      const response = await authFetch(`${API}/sales/${salesId}`, {
        method: "DELETE",
        
      });

      if (response.ok) {
        toast.success("Entry deleted");
        fetchData();
      }
    } catch (_error) {
      toast.error("Failed to delete entry");
    }
  };

  const openEditDialog = (entry) => {
    setEditingEntry(entry);
    setFormData({
      year: entry.year,
      quarter: entry.quarter,
      revenue: entry.revenue.toString(),
      expenses: entry.expenses.toString(),
      units_sold: entry.units_sold?.toString() || "",
      new_customers: entry.new_customers?.toString() || "",
      returning_customers: entry.returning_customers?.toString() || "",
      notes: entry.notes || ""
    });
    setShowDialog(true);
  };

  const resetForm = () => {
    setEditingEntry(null);
    setFormData({
      year: currentYear,
      quarter: 1,
      revenue: "",
      expenses: "",
      units_sold: "",
      new_customers: "",
      returning_customers: "",
      notes: ""
    });
  };

  const handleDownloadPDF = async () => {
    try {
      toast.info("Generating PDF report...");
      const response = await authFetch(`${API}/sales-report/pdf`, {  });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `sales_report_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
        toast.success("PDF downloaded!");
      } else {
        toast.error("Failed to generate PDF");
      }
    } catch (_error) {
      toast.error("Error downloading PDF");
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getGrowthIcon = (value) => {
    if (value > 0) return <ArrowUpRight className="w-4 h-4 text-green-500" />;
    if (value < 0) return <ArrowDownRight className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-neutral-400" />;
  };

  const getGrowthColor = (value) => {
    if (value > 0) return "text-green-600";
    if (value < 0) return "text-red-600";
    return "text-neutral-500";
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="w-8 h-8 border-2 border-emerald-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="sales-tracking-page">
      <div className="bg-white border-b border-neutral-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" size="sm" onClick={() => navigate("/dashboard")} className="text-neutral-600">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Dashboard
              </Button>
              <div>
                <h1 className="text-xl font-semibold text-neutral-900">Sales &amp; Performance</h1>
                <p className="text-sm text-neutral-500">Track quarterly revenue and growth</p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {sales.length > 0 && (
                <Button variant="outline" onClick={handleDownloadPDF} data-testid="download-pdf-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Export PDF
                </Button>
              )}
              
              <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="add-sales-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Quarter
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>{editingEntry ? "Edit Sales Data" : "Add Quarterly Sales"}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 pt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Year</Label>
                        <Select value={formData.year.toString()} onValueChange={(v) => setFormData({ ...formData, year: parseInt(v) })}>
                          <SelectTrigger data-testid="year-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {years.map(y => (<SelectItem key={y} value={y.toString()}>{y}</SelectItem>))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Quarter</Label>
                        <Select value={formData.quarter.toString()} onValueChange={(v) => setFormData({ ...formData, quarter: parseInt(v) })}>
                          <SelectTrigger data-testid="quarter-select"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">Q1 (Jan-Mar)</SelectItem>
                            <SelectItem value="2">Q2 (Apr-Jun)</SelectItem>
                            <SelectItem value="3">Q3 (Jul-Sep)</SelectItem>
                            <SelectItem value="4">Q4 (Oct-Dec)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Revenue ($)</Label>
                        <Input type="number" value={formData.revenue} onChange={(e) => setFormData({ ...formData, revenue: e.target.value })} placeholder="0" data-testid="revenue-input" />
                      </div>
                      <div className="space-y-2">
                        <Label>Expenses ($)</Label>
                        <Input type="number" value={formData.expenses} onChange={(e) => setFormData({ ...formData, expenses: e.target.value })} placeholder="0" data-testid="expenses-input" />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div className="space-y-2">
                        <Label className="text-xs">Units Sold</Label>
                        <Input type="number" value={formData.units_sold} onChange={(e) => setFormData({ ...formData, units_sold: e.target.value })} placeholder="0" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">New Customers</Label>
                        <Input type="number" value={formData.new_customers} onChange={(e) => setFormData({ ...formData, new_customers: e.target.value })} placeholder="0" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Returning</Label>
                        <Input type="number" value={formData.returning_customers} onChange={(e) => setFormData({ ...formData, returning_customers: e.target.value })} placeholder="0" />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Notes (Optional)</Label>
                      <Textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} placeholder="Key highlights, challenges, wins..." className="min-h-16" />
                    </div>

                    {formData.revenue && formData.expenses && (
                      <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                        <div className="flex justify-between text-sm">
                          <span className="text-neutral-600">Calculated Profit:</span>
                          <span className="font-semibold text-emerald-700">{formatCurrency(parseFloat(formData.revenue || 0) - parseFloat(formData.expenses || 0))}</span>
                        </div>
                        <div className="flex justify-between text-sm mt-1">
                          <span className="text-neutral-600">Profit Margin:</span>
                          <span className="font-semibold text-emerald-700">{formData.revenue > 0 ? `${(((parseFloat(formData.revenue) - parseFloat(formData.expenses)) / parseFloat(formData.revenue)) * 100).toFixed(1)}%` : "0%"}</span>
                        </div>
                      </div>
                    )}

                    <Button onClick={handleSubmit} className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={!formData.revenue} data-testid="save-sales-btn">
                      {editingEntry ? "Update Data" : "Save Quarter"}
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Explanation Card */}
        <Card className="border-rose-200 bg-gradient-to-r from-rose-50 to-pink-50 mb-8">
          <CardContent className="p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center flex-shrink-0">
                <BarChart3 className="w-5 h-5 text-rose-600" />
              </div>
              <div>
                <h3 className="font-semibold text-neutral-900 mb-1">Sales & Revenue Tracker</h3>
                <p className="text-sm text-neutral-600 mb-3">
                  Track your sales performance and analyze revenue trends. Log daily, weekly, or monthly sales 
                  with revenue, profit margins, and customer data. View analytics to understand your best-performing 
                  periods, customer acquisition costs, and growth trajectory.
                </p>
                <div className="flex flex-wrap gap-2 text-xs">
                  <span className="px-2 py-1 bg-white rounded border border-rose-200 text-rose-700">Revenue Tracking</span>
                  <span className="px-2 py-1 bg-white rounded border border-rose-200 text-rose-700">Profit Analysis</span>
                  <span className="px-2 py-1 bg-white rounded border border-rose-200 text-rose-700">Customer Metrics</span>
                  <span className="px-2 py-1 bg-white rounded border border-rose-200 text-rose-700">Trend Charts</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {summary?.has_data ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <Card className="border-neutral-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <DollarSign className="w-5 h-5 text-emerald-500" />
                    <span className="text-xs text-neutral-500">Total</span>
                  </div>
                  <p className="text-2xl font-semibold text-neutral-900">{formatCurrency(summary.total_revenue)}</p>
                  <p className="text-sm text-neutral-500">all-time revenue</p>
                </CardContent>
              </Card>
              
              <Card className="border-neutral-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <TrendingUp className="w-5 h-5 text-blue-500" />
                    <span className="text-xs text-neutral-500">Profit</span>
                  </div>
                  <p className="text-2xl font-semibold text-neutral-900">{formatCurrency(summary.total_profit)}</p>
                  <p className="text-sm text-neutral-500">{summary.avg_profit_margin}% avg margin</p>
                </CardContent>
              </Card>
              
              <Card className="border-neutral-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    <Users className="w-5 h-5 text-purple-500" />
                    <span className="text-xs text-neutral-500">Customers</span>
                  </div>
                  <p className="text-2xl font-semibold text-neutral-900">{summary.total_new_customers + summary.total_returning_customers}</p>
                  <p className="text-sm text-neutral-500">{summary.total_new_customers} new, {summary.total_returning_customers} returning</p>
                </CardContent>
              </Card>
              
              <Card className="border-neutral-200">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-2">
                    {getGrowthIcon(summary.yoy_growth)}
                    <span className="text-xs text-neutral-500">YoY Growth</span>
                  </div>
                  <p className={`text-2xl font-semibold ${getGrowthColor(summary.yoy_growth)}`}>
                    {summary.yoy_growth !== null ? `${summary.yoy_growth > 0 ? '+' : ''}${summary.yoy_growth}%` : 'N/A'}
                  </p>
                  <p className="text-sm text-neutral-500">year over year</p>
                </CardContent>
              </Card>
            </div>

            {summary.revenue_goal > 0 && (
              <Card className="border-neutral-200 mb-8">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-2">
                      <Target className="w-5 h-5 text-amber-500" />
                      <h3 className="font-semibold text-neutral-900">{currentYear} Revenue Goal Progress</h3>
                    </div>
                    <span className="text-sm text-neutral-500">{formatCurrency(summary.current_year_revenue)} / {formatCurrency(summary.revenue_goal)}</span>
                  </div>
                  <Progress value={Math.min(summary.goal_progress, 100)} className="h-3" />
                  <p className="text-sm text-neutral-500 mt-2">{summary.goal_progress.toFixed(1)}% of annual goal achieved</p>
                </CardContent>
              </Card>
            )}

            {summary.quarterly_trend?.length > 0 && (
              <Card className="border-neutral-200 mb-8">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg font-semibold flex items-center">
                    <BarChart3 className="w-5 h-5 mr-2 text-emerald-500" />
                    Quarterly Revenue Trend
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <SimpleBarChart data={summary.quarterly_trend} formatCurrency={formatCurrency} />
                </CardContent>
              </Card>
            )}
          </>
        ) : (
          <Card className="border-neutral-200 mb-8">
            <CardContent className="py-12 text-center">
              <BarChart3 className="w-12 h-12 text-neutral-300 mx-auto mb-3" />
              <h3 className="font-medium text-neutral-900 mb-1">No sales data yet</h3>
              <p className="text-neutral-500 mb-4">Add your first quarterly sales data to see insights</p>
              <Button onClick={() => setShowDialog(true)} className="bg-emerald-600 hover:bg-emerald-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Quarter
              </Button>
            </CardContent>
          </Card>
        )}

        <Card className="border-neutral-200">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg font-semibold">Quarterly Data</CardTitle>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Years</SelectItem>
                  {years.map(y => (<SelectItem key={y} value={y.toString()}>{y}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            {sales.length === 0 ? (
              <p className="text-neutral-500 text-center py-8">No data for selected period</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-neutral-200">
                      <th className="text-left py-3 px-2 font-medium text-neutral-600">Period</th>
                      <th className="text-right py-3 px-2 font-medium text-neutral-600">Revenue</th>
                      <th className="text-right py-3 px-2 font-medium text-neutral-600">Expenses</th>
                      <th className="text-right py-3 px-2 font-medium text-neutral-600">Profit</th>
                      <th className="text-right py-3 px-2 font-medium text-neutral-600">Margin</th>
                      <th className="text-right py-3 px-2 font-medium text-neutral-600">Customers</th>
                      <th className="text-center py-3 px-2 font-medium text-neutral-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sales.map((entry) => (
                      <tr key={entry.sales_id} className="border-b border-neutral-100 hover:bg-neutral-50" data-testid={`sales-row-${entry.sales_id}`}>
                        <td className="py-3 px-2"><span className="font-medium text-neutral-900">Q{entry.quarter} {entry.year}</span></td>
                        <td className="py-3 px-2 text-right text-neutral-900">{formatCurrency(entry.revenue)}</td>
                        <td className="py-3 px-2 text-right text-neutral-600">{formatCurrency(entry.expenses)}</td>
                        <td className={`py-3 px-2 text-right font-medium ${entry.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>{formatCurrency(entry.profit)}</td>
                        <td className="py-3 px-2 text-right">
                          <Badge className={entry.profit_margin >= 20 ? 'bg-green-100 text-green-700' : entry.profit_margin >= 10 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>{entry.profit_margin.toFixed(1)}%</Badge>
                        </td>
                        <td className="py-3 px-2 text-right text-neutral-600">{(entry.new_customers || 0) + (entry.returning_customers || 0)}</td>
                        <td className="py-3 px-2 text-center">
                          <div className="flex items-center justify-center space-x-1">
                            <Button variant="ghost" size="sm" onClick={() => openEditDialog(entry)}><Edit className="w-4 h-4" /></Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(entry.sales_id)} className="text-red-500 hover:text-red-600"><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default SalesTrackingPage;
